// src/components/Blocks/PeoplePreview/PeoplePreviewView.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import '../../Views/academicons/css/academicons.min.css';
import '../../Views/icomoon/style.css';
import Image from '@plone/volto/components/theme/Image/Image';
import { flattenRichTextToAppURL } from '../../../helpers/richText';

const PeoplePreviewView = ({ content }) => {
  // Address, keeping its line breaks
  const addressWithLineBreaks = content.address
    ? content.address.replace(/\n/g, '<br>')
    : '';

  return (
    <div className="people-preview-block" data-block-type="people-preview">
      <div
        style={{
          padding: '20px',
          backgroundColor: '#f5f5f5',
          border: '2px dashed #ccc',
          borderRadius: '4px',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#666',
          }}
        >
          📋 EDITOR PREVIEW - This block is not visible in the published view
        </div>

        {/* Header Section */}
        <div className="view-header" style={{ marginTop: '20px' }}>
          {content.type_of_degree && (
            <h3 className="view-header__subtitle">
              {content.type_of_degree.title || content.type_of_degree}
            </h3>
          )}

          <h2 className="view-header__title">
            {content.full_name ? content.full_name : content.id}
          </h2>

          {content.type_of_position && (
            <h3 className="view-header__subtitle">
              {content.type_of_position.title ||
                content.type_of_position.token ||
                content.type_of_position}
            </h3>
          )}
        </div>

        {/* Two-Column Layout: Image + Contact Info */}
        <div className="view-people-layout">
          {/* Portrait Image */}
          {content.image && (
            <div className="view-people-layout__image">
              <Image
                src={
                  content.image?.scales?.large?.download
                    ? flattenToAppURL(content.image.scales.large.download)
                    : '/people_default_image.svg'
                }
                alt={content.image_caption || content.full_name}
              />
            </div>
          )}

          {/* Right Column: Research + Contact + Links */}
          <div className="view-people-layout__content">
            {/* Research Interest */}
            {content.research_areas &&
              content.research_areas?.data !== '<p></p>' && (
                <div className="view-section">
                  <h3 className="view-section__label">Research Interest:</h3>
                  <div
                    className="view-section__richtext"
                    dangerouslySetInnerHTML={{
                      __html: flattenRichTextToAppURL(
                        content.research_areas.data,
                      ),
                    }}
                  />
                </div>
              )}

            {/* Contact Information */}
            {(content.office ||
              content.telephone ||
              content.address ||
              content.email ||
              content.fax) && (
              <div className="view-section">
                <h3 className="view-section__label">Contact:</h3>
                <div className="view-section__contact">
                  {content.office && (
                    <>
                      <span className="view-section__contact-label">Room:</span>
                      <span className="view-section__contact-value">
                        {content.office}
                      </span>
                    </>
                  )}
                  {content.telephone && (
                    <>
                      <span className="view-section__contact-label">Tel.:</span>
                      <span className="view-section__contact-value">
                        {content.telephone}
                      </span>
                    </>
                  )}
                  {content.address && (
                    <>
                      <span className="view-section__contact-label">
                        Address:
                      </span>
                      <span
                        className="view-section__contact-value"
                        dangerouslySetInnerHTML={{
                          __html: flattenRichTextToAppURL(
                            addressWithLineBreaks,
                          ),
                        }}
                      />
                    </>
                  )}
                  {content.email && (
                    <>
                      <span className="view-section__contact-label">
                        Email:
                      </span>
                      <span className="view-section__contact-value">
                        {content.email}
                      </span>
                    </>
                  )}
                  {content.fax && (
                    <>
                      <span className="view-section__contact-label">Fax:</span>
                      <span className="view-section__contact-value">
                        {content.fax}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Social Media Links */}
            {(content.website ||
              content.linkedin ||
              content.github ||
              content.google_scholar ||
              content.orcid_id ||
              content.twitter ||
              content.researchgate) && (
              <div className="view-section">
                <h3 className="view-section__label">Links:</h3>
                <div className="view-section__social-links">
                  {content.website && (
                    <a
                      href={content.website}
                      className="view-section__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Website"
                    >
                      <span className="icon-home"></span>
                    </a>
                  )}
                  {content.twitter && (
                    <a
                      href={content.twitter}
                      className="view-section__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Twitter"
                    >
                      <span className="icon-twitter"></span>
                    </a>
                  )}
                  {content.linkedin && (
                    <a
                      href={content.linkedin}
                      className="view-section__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="LinkedIn"
                    >
                      <span className="icon-linkedin"></span>
                    </a>
                  )}
                  {content.github && (
                    <a
                      href={content.github}
                      className="view-section__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="GitHub"
                    >
                      <span className="icon-github"></span>
                    </a>
                  )}
                  {content.google_scholar && (
                    <a
                      href={content.google_scholar}
                      className="view-section__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Google Scholar"
                    >
                      <i className="ai ai-google-scholar"></i>
                    </a>
                  )}
                  {content.orcid_id && (
                    <a
                      href={content.orcid_id}
                      className="view-section__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="ORCID"
                    >
                      <i className="ai ai-orcid"></i>
                    </a>
                  )}
                  {content.researchgate && (
                    <a
                      href={content.researchgate}
                      className="view-section__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="ResearchGate"
                    >
                      <i className="ai ai-researchgate"></i>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* About Me Section */}
        {content.about && content.about?.data !== '<p></p>' && (
          <div className="view-section">
            <h3 className="view-section__label">About me:</h3>
            <div
              className="view-section__richtext"
              dangerouslySetInnerHTML={{
                __html: flattenRichTextToAppURL(content.about.data),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

PeoplePreviewView.propTypes = {
  content: PropTypes.object.isRequired,
};

export default PeoplePreviewView;
